import type {
  Appearance,
  CatalogResponse,
  HomeLook,
  InventoryItem,
  MyHomeResponse,
  PropertyPublic,
  RealEstateListingsResponse,
  WalletsResponse,
} from "@townsy/contracts";
import type { DecorationPreview, InteriorBoardView, InteriorView, ListingLine, SignVisibilitySample } from "../scene/types";
import type { CameraMode, ChatEntry, RoomInfo, RoomProblem, SceneStatus } from "../state/store";

/**
 * Read-only diagnostics hook for automated end-to-end tests (`tests/e2e`), installed as
 * `window.__townsy` ONLY when the API reports `environment: "local"` (APP_ENV=local). Every
 * method returns a fresh snapshot (plain data, no live references); nothing here can send input,
 * join rooms, chat or change state, so it adds no authority to the browser (docs/02 section 3).
 *
 * Exposed so the acceptance matrix (docs/10, M1 rows A01-A12 / T01-T05) can assert on what the
 * client actually observes: the local predicted position, remote actors' authoritative positions,
 * the current room (kind/key/id/session), chat entries, sign visibility samples from the renderer
 * and the overview framing fit report. Feedback round 1 adds the wallets and Real Estate listings
 * the UI is showing (API data, read-only), the room key, the camera mode, how many times the
 * camera was snapped behind the avatar after a room change, and the last transition (whether the
 * server initiated it, i.e. a walk-through door). The homes round adds the resident's home and
 * inventory, the catalog, a home's free look, the uncommitted placement ghost and anchor markers
 * requested from the renderer, and the home interior (look + furniture) last handed to it. Round 3
 * (ADR-0006) adds the shared town cars as this client knows them, the local driving state, the
 * "Get in" candidate and door highlight, and the store theme last handed to the renderer. ADR-0018
 * adds each actor's posture and seat (sitting / lying) and the local seating and running state. World v5
 * (ADR-0041) adds the current district; town v6 (ADR-0042: one street, no gates) the district of the avatar's
 * position and of the overview camera, and the free overview camera's state (bounds, district framings, last input).
 * ADR-0054 adds what the renderer drew in its latest frame (the cars and the walk / chase camera) for the drive probe.
 */
export interface HookActor {
  actorId: string;
  label: string;
  x: number;
  z: number;
  facing: number;
  /** 0 idle, 1 walking, 2 running (ADR-0018). */
  anim: number;
  isLocal: boolean;
  /** 0 standing, 1 sitting, 2 lying (ActorState.posture as this client draws it). */
  posture: number;
  /** The seat while sitting / lying ("" while standing). */
  seatId: string;
}

/** ADR-0018: what the local player is offered / doing (seats of the current room, the bottom prompt, running). */
export interface HookSeating {
  /** Seats of the current room as this client lists them (shared packages/world lists). */
  seatCount: number;
  /** The seat the bottom prompt offers ("Sit on the sofa"), or null. */
  offer: { seatId: string; kind: "sit" | "lie"; place: string } | null;
  /** The local avatar's posture from the server, or null when standing. */
  seated: { kind: "sit" | "lie"; place: string | null } | null;
  pending: "sit" | "lie" | "stand" | null;
  /** Shift held or the touch Run toggle on (the run bit this client sends). */
  running: boolean;
  /** Seat ids with their kind and position (for tests that walk to a seat). */
  seats: Array<{ id: string; kind: "sit" | "lie"; x: number; z: number; place: string }>;
}

export interface HookVehicle {
  vehicleId: string;
  style: number;
  color: number;
  mode: string;
  x: number;
  z: number;
  heading: number;
  speed: number;
  driverActorId: string;
  /** The car this client drives (pose = its prediction); every other car is the latest server state. */
  isLocal: boolean;
}

export interface HookDriving {
  vehicleId: string;
  pending: "enter" | "exit" | null;
  /** "Get out" pending: the server is braking the car; prediction follows it. */
  held: boolean;
  pendingCount: number;
  acknowledgedSeq: number;
  lastCorrection: number;
  snaps: number;
}

/** The visitor's district as this client sees it (town v6, ADR-0042: derived from the avatar's position). */
export interface HookDistrict {
  /** The session's current district (on the street: of the avatar's position; inside: of the building). */
  current: string;
  /** The store's copy (what the HUD, map and directory read). */
  store: string;
  /** The district the local avatar stands in on the street (null inside, before the first state, or between districts). */
  byPosition: string | null;
  /** The district the overview camera's target is in (null before the scene). */
  overview: string | null;
}

/** Town v6 (ADR-0042 decision 4): the free overview camera. */
export interface HookOverview {
  /** The district the camera's target is in. */
  district: string | null;
  /** Where the target is kept (the whole town's bounds on the street). */
  bounds: { minX: number; minZ: number; maxX: number; maxZ: number };
  /** What moved the camera last: "drag" | "orbit" | "pinch" | "wheel" | "keys" | "home" | "frame" (null: nothing yet). */
  lastInput: string | null;
  /** Each district's fitted framing (what frameDistrict / the quick buttons glide to; the first is Home). */
  framings: Record<string, { alpha: number; beta: number; radius: number; target: { x: number; y: number; z: number } }>;
}

export interface TownTestHook {
  readonly version: 1;
  readonly actorId: string;
  status(): string;
  sessionId(): string | null;
  roomId(): string | null;
  /** Canonical room key from room.welcome, e.g. "street:2:central:1" or "interior:v-01". */
  roomKey(): string | null;
  room(): RoomInfo;
  roomProblem(): RoomProblem | null;
  /** Local avatar as predicted on this client (null before the first authoritative state). */
  local(): HookActor | null;
  /** Appearance the renderer uses for the local avatar (from the authoritative room state). */
  localAppearance(): Appearance | null;
  /** Everyone the client currently knows about in the room (local actor = predicted position). */
  actors(): HookActor[];
  mode(): CameraMode;
  /** Same as mode(): "overview" | "walk". */
  cameraMode(): CameraMode;
  /** Times the walk camera was snapped behind the avatar (once per room change / reconnect). */
  cameraSnaps(): number;
  /** `districtId` is the district a street ticket names (the square or door it lands at). */
  lastTransition(): { roomName: string; propertyId: string | null; districtId: string | null; serverInitiated: boolean; at: number } | null;
  /** The current district (town v6: of the avatar's position), the store's copy and the overview camera's district. */
  district(): HookDistrict;
  /** Town v6: the free overview camera (null before the scene is ready). */
  overviewState(): HookOverview | null;
  /**
   * ADR-0043: the Overview focus this tab last told the street room (`view.focus`; null: none; undefined: nothing sent
   * in this membership) and how many it sent.
   */
  viewFocus(): { lastSent: { x: number; z: number; radius: number } | null | undefined; sent: number };
  sceneStatus(): SceneStatus;
  cameraYaw(): number | null;
  fps(): number;
  /** Renderer sign visibility samples for the active camera (null before the scene is ready). */
  signSamples(): SignVisibilitySample[] | null;
  fitReport(): unknown;
  /** Chat lines of the current room; `at` is the server's timestamp of the line (round 5: AI resident timing). */
  chat(): Array<Pick<ChatEntry, "id" | "actorId" | "label" | "text" | "own" | "system" | "at">>;
  /** Last `GET /me/wallets` the HUD shows (null for guests / not loaded). Coins and Credit stay separate. */
  wallets(): WalletsResponse | null;
  /** Last `GET /real-estate/listings` (null until loaded). */
  listings(): RealEstateListingsResponse | null;
  /** Residential plots the client prediction treats as owned (home colliders). */
  ownedResidential(): string[];
  /** Authoritative appearance of any actor in the room as this client renders it (null if unknown). */
  appearanceOf(actorId: string): Appearance | null;
  /** The public property DTO this client holds (store copy; null if not loaded). */
  property(id: string): PropertyPublic | null;
  /** The store board / office listings the renderer was last told to show inside a room (null on the street). */
  interiorBoard(): { propertyId: string; board: InteriorBoardView | null; listings: ListingLine[] | null } | null;
  /** Overview camera framing (radius in metres); null before the scene is ready. */
  overviewCamera(): { radius: number; alpha: number; beta: number; target: { x: number; y: number; z: number } } | null;
  /** Shared town cars (server-simulated room state, ADR-0006; never actors): positions and speeds. */
  cars(): Array<{ x: number; z: number; speed: number }>;
  /** Shared town cars with their mode and driver (the local driven car from prediction). */
  vehicles(): HookVehicle[];
  /** The car this client drives and its prediction diagnostics (null while walking). */
  driving(): HookDriving | null;
  /** The car offered by the "Get in (E)" prompt (null when none). */
  vehiclePrompt(): string | null;
  /** The car whose driver's door the renderer was asked to highlight (null when none). */
  doorHighlight(): string | null;
  /** The store theme last handed to the renderer inside a store (null elsewhere). */
  interiorStoreTheme(): string | null;
  /** Rendered height (metres) of each commercial building by property id. */
  buildingHeights(): Record<string, number>;
  /**
   * M5: the renderer's business sign textures, one per placement version (`key` = "placementId:presentationVersion"):
   * whether the approved logo is shown, still loading, failed or absent (monogram). Null before the scene is ready.
   */
  rendererSigns(): Array<{ key: string; business: string; logo: string; reason: string | null; refs: number }> | null;
  /** M5: what each billboard shows in the renderer (content requested, face shown, image load state). */
  rendererBillboards(): Array<{ id: string; content: string; shown: string; pending: string | null; near: boolean; image: { url: string; status: string; reason: string | null } | null }> | null;
  /**
   * Round 5 (ADR-0010): an actor's speech bubble and name tag as the renderer draws them (the bubble's
   * current text, its on-screen box while showing, the tag's screen position, and whether the tag is an
   * AI resident's tag with the "AI" badge). Null when the actor is not in the scene or the scene is not ready.
   */
  speech(actorId: string): {
    text: string;
    bubble: { cx: number; cy: number; heightPx: number } | null;
    /** The painted bubble (body and tail) on screen, render px; null when not drawn. */
    rect: { left: number; top: number; right: number; bottom: number } | null;
    tag: { cx: number; cy: number; heightPx: number } | null;
    ai: boolean;
  } | null;
  ui(): {
    selectedPropertyId: string | null;
    nearbyEntranceId: string | null;
    transitionPending: boolean;
    chatFocused: boolean;
    chatOpen: boolean;
    statsOpen: boolean;
    walletOpen: boolean;
    realEstateOpen: boolean;
    homeBuildersOpen: boolean;
    furnishingsOpen: boolean;
    myHomeOpen: boolean;
    myHomeTab: "yard" | "interior";
    /** M6: the "Earn coins" list, the in-store Tasks panel (property id) and the task dialog. */
    earnOpen: boolean;
    storeTasksFor: string | null;
    taskOpen: boolean;
  };
  /** Last `GET /me/home` (null for guests, residents without a plot, or not loaded). */
  myHome(): NonNullable<MyHomeResponse["home"]> | null;
  /** Last `GET /me/inventory` items (null when not loaded). */
  inventory(): InventoryItem[] | null;
  /** Last `GET /catalog` (null until loaded). */
  catalog(): CatalogResponse | null;
  /** Free look of a home from the public property DTO (default: the resident's own home). */
  homeLook(propertyId?: string): HomeLook | null;
  /** The uncommitted placement ghost currently requested from the renderer (null = none). */
  decorationPreview(): DecorationPreview | null;
  /** Anchor markers currently requested from the renderer (null = none). */
  highlightedAnchors(): { propertyId: string; scope: "yard" | "interior"; anchorIds: string[] } | null;
  /** The home interior (stage, look, furniture) last handed to the renderer; null outside homes. */
  interiorHome(): { propertyId: string; home: InteriorView["home"] } | null;
  prediction(): { pendingCount: number; acknowledgedSeq: number; lastCorrection: number; colliderCount: number } | null;
  /** Attractions (ADR-0016): the venue game state this client holds (shared + own + chips + recent notices). */
  venue(): { venue: string | null; seq: number; shared: unknown; mine: unknown; chips: number | null; events: Array<{ type: string; code: string | null; message: string }> };
  /** ADR-0018: seats, the sit / lie prompt, the local posture and running. */
  seating(): HookSeating;
  /**
   * ADR-0054: what the renderer drew in its latest frame: every car's drawn pose (the local car on its step timeline,
   * the others interpolated) and the walk / chase camera (position and target). Null before the scene is ready.
   */
  drawnCars(): HookDrawnCars | null;
  /**
   * Calls `listener` with the predicted position after every batch of local walking steps (the movement loop reads the
   * keys once per batch), until the returned function is called. e2e walk helpers run their stop check here: a check
   * on a timer can run after a frame's batch AND a late timer's batch in a starved software-rendered page (ADR-0055).
   */
  onLocalTick(listener: (p: { x: number; z: number }) => void): () => void;
}

/** ADR-0054 (driving near other cars): the renderer's drawn cars and the walk camera, for frame-by-frame probes. */
export interface HookDrawnCars {
  cars: Array<{ id: string; x: number; z: number; heading: number; speed: number; isLocal: boolean; fade: number }>;
  camera: { x: number; y: number; z: number; tx: number; ty: number; tz: number } | null;
  /** ADR-0055: the local avatar as drawn this frame (its smoothed position), or null. */
  avatar?: { x: number; z: number; facing: number } | null;
  /** ADR-0055: inside a venue with vehicles (the truck arena), the local driver's vehicle as drawn this frame, or null. */
  venueVehicle?: { x: number; z: number; heading: number; speed: number } | null;
  /**
   * ADR-0055: when the frame these poses were drawn in started (performance.now). A probe's own requestAnimationFrame
   * callback can run before or after the renderer's in the same vsync, so it may read one drawn frame twice and skip
   * none: keyed by this, every drawn frame is counted once, timed by when it was drawn.
   */
  frameAt?: number;
}

export type TownTestHookSource = {
  [K in Exclude<keyof TownTestHook, "version">]: TownTestHook[K] extends (...args: infer A) => infer R ? (...args: A) => R : TownTestHook[K];
};

declare global {
  interface Window {
    __townsy?: TownTestHook;
  }
}

function clone<T>(value: T): T {
  return value === null || value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);
}

/** Installs the hook and returns the function that removes it again (session disposed). */
export function installTestHook(source: TownTestHookSource): () => void {
  if (typeof window === "undefined") return () => {};
  const hook: TownTestHook = Object.freeze({
    version: 1 as const,
    actorId: source.actorId,
    status: () => source.status(),
    sessionId: () => source.sessionId(),
    roomId: () => source.roomId(),
    roomKey: () => source.roomKey(),
    room: () => ({ ...source.room() }),
    roomProblem: () => {
      const p = source.roomProblem();
      return p ? { ...p } : null;
    },
    local: () => {
      const a = source.local();
      return a ? { ...a } : null;
    },
    localAppearance: () => {
      const a = source.localAppearance();
      return a ? { ...a } : null;
    },
    actors: () => source.actors().map((a) => ({ ...a })),
    mode: () => source.mode(),
    cameraMode: () => source.cameraMode(),
    cameraSnaps: () => source.cameraSnaps(),
    lastTransition: () => {
      const t = source.lastTransition();
      return t ? { ...t } : null;
    },
    district: () => clone(source.district()),
    overviewState: () => clone(source.overviewState()),
    viewFocus: () => {
      const v = source.viewFocus();
      return { lastSent: v.lastSent ? { ...v.lastSent } : v.lastSent, sent: v.sent };
    },
    sceneStatus: () => source.sceneStatus(),
    cameraYaw: () => source.cameraYaw(),
    fps: () => source.fps(),
    signSamples: () => {
      const s = source.signSamples();
      return s ? s.map((x) => ({ ...x })) : null;
    },
    fitReport: () => {
      const r = source.fitReport();
      return r && typeof r === "object" ? JSON.parse(JSON.stringify(r)) : r;
    },
    chat: () => source.chat().map((m) => ({ id: m.id, actorId: m.actorId, label: m.label, text: m.text, own: m.own, system: m.system, at: m.at })),
    wallets: () => clone(source.wallets()),
    listings: () => clone(source.listings()),
    ownedResidential: () => [...source.ownedResidential()],
    appearanceOf: (actorId: string) => clone(source.appearanceOf(actorId)),
    property: (id: string) => clone(source.property(id)),
    interiorBoard: () => clone(source.interiorBoard()),
    overviewCamera: () => clone(source.overviewCamera()),
    cars: () => source.cars().map((c) => ({ x: c.x, z: c.z, speed: c.speed })),
    vehicles: () => source.vehicles().map((v) => ({ ...v })),
    driving: () => {
      const d = source.driving();
      return d ? { ...d } : null;
    },
    vehiclePrompt: () => source.vehiclePrompt(),
    doorHighlight: () => source.doorHighlight(),
    interiorStoreTheme: () => source.interiorStoreTheme(),
    buildingHeights: () => ({ ...source.buildingHeights() }),
    rendererSigns: () => clone(source.rendererSigns()),
    rendererBillboards: () => clone(source.rendererBillboards()),
    speech: (actorId: string) => clone(source.speech(actorId)),
    ui: () => ({ ...source.ui() }),
    myHome: () => clone(source.myHome()),
    inventory: () => clone(source.inventory()),
    catalog: () => clone(source.catalog()),
    homeLook: (propertyId?: string) => clone(source.homeLook(propertyId)),
    decorationPreview: () => clone(source.decorationPreview()),
    highlightedAnchors: () => clone(source.highlightedAnchors()),
    interiorHome: () => clone(source.interiorHome()),
    prediction: () => {
      const p = source.prediction();
      return p ? { ...p } : null;
    },
    venue: () => clone(source.venue()),
    seating: () => clone(source.seating()),
    drawnCars: () => clone(source.drawnCars()),
    onLocalTick: (listener: (p: { x: number; z: number }) => void) => source.onLocalTick(listener),
  });
  window.__townsy = hook;
  return () => {
    if (window.__townsy === hook) delete window.__townsy;
  };
}
